import type { Client } from '@urql/core'
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx'
import { graphql } from 'app/generated/gql'
import type { Notification as NotificationType, NotificationListResult } from 'app/generated/graphql'
import { EntityStore } from './EntityStore'

// The typed tag needs a static document, so the Notification field list is written out
// in each operation instead of interpolated.
const LIST_QUERY = graphql(`
  query Notifications($search: String, $pagination: PaginationInput, $sort: SortInput) {
    notifications(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        activityId
        archived
        clicked
        contactId
        createdDate
        id
        locationId
        managedNotification
        medium
        messageId
        objectType
        orgId
        read
        triggerDate
        types
        updatedDate
        userId
        version
        workflowId
        workflowReason
        workflowStep
        workspaceId
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Notification($id: ID!) {
    notification(id: $id) {
      activityId
      archived
      clicked
      contactId
      createdDate
      id
      locationId
      managedNotification
      medium
      messageId
      objectType
      orgId
      read
      triggerDate
      types
      updatedDate
      userId
      version
      workflowId
      workflowReason
      workflowStep
      workspaceId
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateNotification($id: ID!, $input: UpdateNotificationInput!) {
    updateNotification(id: $id, input: $input) {
      activityId
      archived
      clicked
      contactId
      createdDate
      id
      locationId
      managedNotification
      medium
      messageId
      objectType
      orgId
      read
      triggerDate
      types
      updatedDate
      userId
      version
      workflowId
      workflowReason
      workflowStep
      workspaceId
    }
  }
`)

// The unread badge total, counted server-side (`!read:true`, limit 0) so it covers every
// unread notification, not only the pages the panel has loaded. This is the single source
// (ADR-0002) the bell badge and the sidebar count both read as `store.unreadCount`.
const UNREAD_COUNT_QUERY = graphql(`
  query NotificationUnreadCount {
    notifications(search: "!read:true", pagination: { limit: 0 }) {
      total
    }
  }
`)

// Mark every app-medium notification read in one server pass. Returns a bare success flag
// (no notification rows), so the store reconciles the count with a fresh fetchUnreadCount.
const MARK_ALL_READ_MUTATION = graphql(`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      success
    }
  }
`)

// Zero the user's "unseen" marker (the tab-title / "new since you last looked" concept),
// distinct from per-notification read state. Called when the panel opens or a mark happens.
const RESET_UNSEEN_MUTATION = graphql(`
  mutation ResetNotificationUnseen {
    resetNotificationUnseen {
      success
    }
  }
`)

export class NotificationStore extends EntityStore<
  NotificationType,
  NotificationListResult,
  undefined,
  typeof UPDATE_MUTATION
> {
  /**
   * Server-reported count of unread notifications (`!read:true`), the single source the bell
   * badge and the sidebar count both read (ADR-0002). It stays `undefined` until the first
   * server seed: `adjustUnreadCount` is a no-op while unseeded, so an optimistic mark-read
   * fired before the seed cannot invent a count that then disagrees with the server. The badge
   * consumer reads `unreadBadgeCount` (`?? 0`), so the UI shows 0 until the seed lands.
   */
  unreadCount: number | undefined = undefined

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      updateMutation: UPDATE_MUTATION,
      extractList: (data: any) => data.notifications,
      extractOne: (data: any) => data.notification,
      extractMutated: (data: any) => data.updateNotification,
    })
    // The base ctor already called makeObservable on the inherited fields; declare only
    // this subclass's own members here (MobX forbids re-annotating inherited keys).
    makeObservable(this, {
      unreadCount: observable,
      hasUnreadCount: computed,
      unreadBadgeCount: computed,
      fetchUnreadCount: action.bound,
      setUnreadCount: action.bound,
      adjustUnreadCount: action.bound,
      markRead: action.bound,
      markAllRead: action.bound,
      resetUnseen: action.bound,
      reset: override,
    } as any)
  }

  /** True once the unread count has been seeded from the server at least once. */
  get hasUnreadCount(): boolean {
    return this.unreadCount !== undefined
  }

  /** The unread count for the badge: the seeded value, or 0 before the first seed. */
  get unreadBadgeCount(): number {
    return this.unreadCount ?? 0
  }

  /** Read the unread total from the server and store it as the shared badge source. */
  async fetchUnreadCount(opts?: { requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' }) {
    const result = await this.client
      .query(UNREAD_COUNT_QUERY, {}, { requestPolicy: opts?.requestPolicy ?? 'network-only' })
      .toPromise()
    runInAction(() => {
      const total = result.data?.notifications?.total
      if (typeof total === 'number') this.unreadCount = total
    })
    return result
  }

  /** Overwrite the unread count (e.g. mark-all-read sets it to 0). */
  setUnreadCount(value: number) {
    this.unreadCount = Math.max(0, value)
  }

  /** Move the unread count by `delta`, clamped at 0. No-op until the count is seeded. */
  adjustUnreadCount(delta: number) {
    if (this.unreadCount === undefined) return
    this.unreadCount = Math.max(0, this.unreadCount + delta)
  }

  /**
   * Mark one notification read/unread with the ADR-0002 optimistic-then-reconcile pattern:
   * merge the new `read` flag into the shared interned instance and adjust the unread count
   * at once, send the mutation, then reconcile from the server (on failure, roll the count
   * and the flag back). Every view that reads this instance or `unreadCount` re-renders.
   */
  async markRead(id: string, read: boolean) {
    const instance = this.lookup(id)
    const wasRead = instance?.read ?? !read
    // Only a real read-state change shifts the unread total.
    const delta = read === wasRead ? 0 : read ? -1 : 1
    runInAction(() => {
      if (instance) this.applyPatch(id, { read } as Partial<NotificationType>)
      this.adjustUnreadCount(delta)
    })
    const result = await this.update(id, { read })
    if (result.error) {
      runInAction(() => {
        if (instance) this.applyPatch(id, { read: wasRead } as Partial<NotificationType>)
        this.adjustUnreadCount(-delta)
      })
    }
    return result
  }

  /**
   * Mark every loaded notification read and zero the unread count optimistically, then run the
   * server pass and reconcile the count from the server (mark-all also touches notifications not
   * loaded here, so the fresh fetch is authoritative). Pass the currently loaded hits to patch.
   */
  async markAllRead() {
    runInAction(() => {
      for (const n of this.hits) if (n.id) this.applyPatch(n.id, { read: true } as Partial<NotificationType>)
      this.setUnreadCount(0)
    })
    const result = await this.client.mutation(MARK_ALL_READ_MUTATION, {}).toPromise()
    await this.fetchUnreadCount()
    return result
  }

  /**
   * Zero the user's "unseen" marker server-side. This is the tab-title / "new since you last
   * looked" concept, separate from per-notification read state and from `unreadCount`.
   */
  async resetUnseen() {
    return this.client.mutation(RESET_UNSEEN_MUTATION, {}).toPromise()
  }

  reset() {
    super.reset()
    this.unreadCount = undefined
  }
}
